/**
 * The family card (#1459) and its actions, for any page that shows one: the user view, under the user's subscription,
 * and the Families page (#1809). It reads through adminGetFamilyDetails and keeps what it read across re-renders, so a
 * tab switch does not read again. familyDetailsPanel.js draws it; this module owns its state and buttons.
 */

import { escapeHTML, familyCardHTML, familyLoadingHTML, familyMessageHTML } from "./familyDetailsPanel.js";
import { openFamilyImportDialog } from "./familyImport.js";
import { openFamilyRecipe } from "./familyRecipe.js";
import { openFamilyImportBatch } from "./familyImportBatch.js";

/**
 * @param {object} options
 * @param {string} options.containerId the element the card draws into
 * @param {() => object} options.getContext app.js's context (functions, httpsCallable, showToast)
 * @param {() => string|null} [options.getCurrentUid] the user the page is about, marked "This user" and acting for imports
 * @param {(uid: string) => void} options.onLookupUser a member's Look up button
 * @param {(familyId: string) => string} [options.missingHTML] what to draw when the family document does not exist
 * @param {(details: object|null) => void} [options.onLoaded] called after every read, with what it returned
 * @param {string} [options.defaultTab] the tab a newly read family opens on
 */
export function createFamilyCard({ containerId, getContext, getCurrentUid = () => null, onLookupUser, missingHTML, onLoaded, defaultTab = "plan" }) {
  let state = { familyId: null, details: null, error: null, tab: defaultTab };

  const container = () => document.getElementById(containerId);

  function reset() {
    state = { familyId: null, details: null, error: null, tab: defaultTab };
  }

  /** Draws what was read for this family, or reads it now; a read in flight draws when it lands. */
  function show(familyId) {
    if (state.familyId === familyId) {
      if (state.details || state.error) draw();
      else if (container()) container().innerHTML = familyLoadingHTML(familyId);
      return;
    }
    load(familyId);
  }

  /** Draws details read elsewhere (the Families page's search reads them to find the family), without reading again. */
  function showDetails(familyId, details) {
    state = { familyId, details: { ...details, fetched_at: new Date().toISOString() }, error: null, tab: defaultTab };
    draw();
  }

  async function load(familyId) {
    const el = container();
    if (!el) return;
    state = { familyId, details: null, error: null, tab: state.familyId === familyId ? state.tab : defaultTab };
    el.innerHTML = familyLoadingHTML(familyId);

    const { functions, httpsCallable } = getContext() || {};
    try {
      if (!functions || typeof httpsCallable !== "function") throw new Error("Cloud Functions are not available.");
      const res = await httpsCallable(functions, "adminGetFamilyDetails")({ familyId });
      // Another search may have started while this one read.
      if (state.familyId !== familyId) return;
      state.details = { ...(res.data || {}), fetched_at: new Date().toISOString() };
    } catch (err) {
      console.error("❌ adminGetFamilyDetails failed:", err);
      if (state.familyId !== familyId) return;
      state.error = err.message || String(err);
    }
    onLoaded?.(state.error ? null : state.details);
    draw();
  }

  function draw() {
    const el = container();
    if (!el) return;
    const { familyId, details, error, tab } = state;
    if (error) {
      el.innerHTML = familyMessageHTML(familyId, "Could not read the family", escapeHTML(error), "red");
    } else if (!details?.found) {
      el.innerHTML = missingHTML
        ? missingHTML(familyId)
        : familyMessageHTML(familyId, "No such family", `Nothing at <code class="font-mono text-xs">families/${escapeHTML(familyId)}</code>.`);
    } else {
      el.innerHTML = familyCardHTML(details, tab, getCurrentUid());
    }
    attachEvents(el);
  }

  function attachEvents(el) {
    const { showToast } = getContext() || {};
    el.querySelectorAll("[data-family-tab]").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.tab = btn.dataset.familyTab;
        draw();
      });
    });
    el.querySelector("[data-family-refresh]")?.addEventListener("click", () => load(state.familyId));
    el.querySelector("[data-family-retry]")?.addEventListener("click", () => load(state.familyId));
    el.querySelector("[data-family-copy-id]")?.addEventListener("click", () => {
      navigator.clipboard.writeText(state.familyId || "");
      showToast?.("Copied the family ID.", "info");
    });
    // Import recipes for this household (#1562), and the Imports tab's actions on the new path's imports and batches
    const reloadFamily = () => load(state.familyId);
    el.querySelector("[data-family-import]")?.addEventListener("click", () => {
      if (!state.details) return;
      openFamilyImportDialog({ details: state.details, currentUid: getCurrentUid(), onDone: reloadFamily });
    });
    el.querySelectorAll("[data-family-paste-import]").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (!state.details) return;
        openFamilyImportDialog({
          details: state.details,
          currentUid: getCurrentUid(),
          onDone: reloadFamily,
          pasteImport: { import_id: btn.dataset.familyPasteImport, url: btn.dataset.familyPasteUrl || null }
        });
      });
    });
    el.querySelectorAll("[data-family-paste-failed]").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (!state.details) return;
        openFamilyImportDialog({
          details: state.details,
          currentUid: getCurrentUid(),
          onDone: reloadFamily,
          pasteImport: { import_id: btn.dataset.familyPasteFailed, url: btn.dataset.familyPasteUrl, failure_reason: btn.dataset.familyPasteReason, failed: true }
        });
      });
    });
    // A batch opens on every recipe in it (#2334)
    el.querySelectorAll("[data-family-batch-open]").forEach((btn) => {
      btn.addEventListener("click", () => openFamilyImportBatch({ familyId: state.familyId, batchId: btn.dataset.familyBatchOpen }));
    });
    el.querySelectorAll("[data-family-batch-push]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        if (!window.confirm("Send this batch's push now, with what was added so far? It goes once.")) return;
        const { functions, httpsCallable } = getContext();
        btn.disabled = true;
        try {
          const res = await httpsCallable(functions, "adminSmartImport")({
            action: "send_batch_push", family_id: state.familyId, batch_id: btn.dataset.familyBatchPush
          });
          showToast(`Push sent: ${res.data.completed} recipe${res.data.completed === 1 ? "" : "s"} added.`, "success");
          reloadFamily();
        } catch (err) {
          console.error("❌ send_batch_push failed:", err);
          showToast(`Couldn't send the push: ${err.message}`, "error");
          btn.disabled = false;
        }
      });
    });
    // Stuck imports ended (#1847): they fail, so the apps stop showing them as processing. No push is sent.
    const endStuck = async (btn, target, question) => {
      if (!window.confirm(question)) return;
      const { functions, httpsCallable } = getContext();
      btn.disabled = true;
      try {
        const res = await httpsCallable(functions, "adminSmartImport")({ action: "end_stuck", family_id: state.familyId, ...target });
        const { ended, skipped } = res.data;
        const moving = skipped ? ` ${skipped} moved in the last hour and ${skipped === 1 ? "was" : "were"} left.` : "";
        showToast(`Ended ${ended} import${ended === 1 ? "" : "s"}.${moving}`, ended ? "success" : "info");
        reloadFamily();
      } catch (err) {
        console.error("❌ adminSmartImport end_stuck failed:", err);
        showToast(`Couldn't end it: ${err.message}`, "error");
        btn.disabled = false;
      }
    };
    el.querySelectorAll("[data-family-end-import]").forEach((btn) => {
      btn.addEventListener("click", () => endStuck(btn, { import_id: btn.dataset.familyEndImport },
        "End this import? It shows as failed in the app, with \"Something went wrong on our side\". Its batch sends no push after this."));
    });
    el.querySelectorAll("[data-family-end-batch]").forEach((btn) => {
      btn.addEventListener("click", () => endStuck(btn, { batch_id: btn.dataset.familyEndBatch },
        "End every import of this batch that has stood still for an hour? They show as failed in the app. The batch sends no push after this: send it first if you want it."));
    });
    // A draft made a Smart recipe (#1607): from its link when it has one, else (or when asked) from its own text
    el.querySelectorAll("[data-family-convert]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const fromText = btn.dataset.familyConvertFrom === "text";
        const source = fromText || !btn.dataset.familyConvertLink ? "its own ingredients and steps" : "its link";
        if (!window.confirm(`Convert "${btn.dataset.familyConvertTitle}" to a Smart recipe, from ${source}? The Smart recipe replaces the draft. No push is sent.`)) return;
        const { functions, httpsCallable } = getContext();
        btn.disabled = true;
        try {
          const res = await httpsCallable(functions, "adminSmartImport")({
            action: "convert",
            family_id: state.familyId,
            recipe_id: btn.dataset.familyConvert,
            from: fromText ? "text" : "auto",
            user_id: getCurrentUid() || undefined
          });
          const item = res.data.items?.[0] || {};
          if (item.result === "ready") showToast(`Converted: "${item.recipe_title}" is a Smart recipe now.`, "success");
          else if (item.result === "failed") showToast(`Couldn't convert it: ${item.failure_reason || "the import failed"}`, "error");
          else showToast(`Converting from ${res.data.from === "link" ? "its link" : "its text"}. Follow it on the Imports tab.`, "info");
          reloadFamily();
        } catch (err) {
          console.error("❌ adminSmartImport convert failed:", err);
          showToast(`Couldn't convert it: ${err.message}`, "error");
          btn.disabled = false;
        }
      });
    });
    // Ticked drafts converted together (#2471): the same `convert` per recipe, a few at a time, then one summary
    const selectBoxes = [...el.querySelectorAll("[data-family-select]")];
    const selectAll = el.querySelector("[data-family-select-all]");
    const convertSelected = el.querySelector("[data-family-convert-selected]");
    const syncSelection = () => {
      const ticked = selectBoxes.filter((box) => box.checked).length;
      if (selectAll) {
        selectAll.checked = ticked > 0 && ticked === selectBoxes.length;
        selectAll.indeterminate = ticked > 0 && ticked < selectBoxes.length;
      }
      if (convertSelected) {
        convertSelected.disabled = ticked === 0;
        convertSelected.querySelector("[data-family-convert-selected-label]").textContent = ticked ? `Convert ${ticked} selected` : "Convert selected";
      }
    };
    selectBoxes.forEach((box) => box.addEventListener("change", syncSelection));
    selectAll?.addEventListener("change", () => {
      selectBoxes.forEach((box) => { box.checked = selectAll.checked; });
      syncSelection();
    });
    convertSelected?.addEventListener("click", async () => {
      const picked = selectBoxes.filter((box) => box.checked)
        .map((box) => ({ id: box.dataset.familySelect, title: box.dataset.familySelectTitle }));
      if (!picked.length) return;
      const count = `${picked.length} recipe${picked.length === 1 ? "" : "s"}`;
      if (!window.confirm(`Convert ${count} to Smart recipes? Each from its link when it has one, else from its own ingredients and steps. The Smart recipes replace the drafts. No push is sent.`)) return;
      const { functions, httpsCallable } = getContext();
      const label = convertSelected.querySelector("[data-family-convert-selected-label]");
      [convertSelected, selectAll, ...selectBoxes].forEach((control) => { if (control) control.disabled = true; });
      const outcome = { ready: 0, started: 0, failed: [] };
      let next = 0;
      let finished = 0;
      const worker = async () => {
        while (next < picked.length) {
          const recipe = picked[next++];
          try {
            const res = await httpsCallable(functions, "adminSmartImport")({
              action: "convert",
              family_id: state.familyId,
              recipe_id: recipe.id,
              from: "auto",
              user_id: getCurrentUid() || undefined
            });
            const item = res.data.items?.[0] || {};
            if (item.result === "ready") outcome.ready++;
            else if (item.result === "failed") outcome.failed.push(`"${recipe.title}": ${item.failure_reason || "the import failed"}`);
            else outcome.started++;
          } catch (err) {
            console.error(`❌ adminSmartImport convert of ${recipe.id} failed:`, err);
            outcome.failed.push(`"${recipe.title}": ${err.message}`);
          }
          label.textContent = `Converting… ${++finished} of ${picked.length}`;
        }
      };
      label.textContent = `Converting… 0 of ${picked.length}`;
      await Promise.all(Array.from({ length: Math.min(3, picked.length) }, worker));
      const parts = [];
      if (outcome.ready) parts.push(`${outcome.ready} converted`);
      if (outcome.started) parts.push(`${outcome.started} converting (follow them on the Imports tab)`);
      if (outcome.failed.length) parts.push(`${outcome.failed.length} couldn't be converted: ${outcome.failed.join("; ")}`);
      showToast(`${parts.join(", ")}.`, outcome.failed.length ? (outcome.failed.length === picked.length ? "error" : "warning") : "success");
      reloadFamily();
    });
    // A recipe's title opens what it holds (#1841)
    el.querySelectorAll("[data-family-recipe]").forEach((btn) => {
      btn.addEventListener("click", () => openFamilyRecipe({
        familyId: state.familyId,
        recipeId: btn.dataset.familyRecipe,
        title: btn.dataset.familyRecipeTitle,
        members: state.details?.members || [],
        // An edit saved from the drawer (#1843) changes the title and the summary the card lists.
        onSaved: () => load(state.familyId)
      }));
    });
    el.querySelectorAll("[data-family-lookup-uid]").forEach((btn) => {
      btn.addEventListener("click", () => onLookupUser(btn.dataset.familyLookupUid));
    });
  }

  return { show, showDetails, load, reset, get state() { return state; } };
}
